import {
  Boxes,
  Database,
  KeyRound,
  RefreshCw,
  Search,
  ShieldCheck,
  Upload,
} from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** Configuring the embedding provider is Founder / Super Admin work. */
const ADMIN_ROLES: Role[] = ['super_admin', 'founder'];

export const KB_GUIDES: Guide[] = [
  {
    slug: 'kb-overview',
    title: 'What the Knowledge Base is',
    module: 'kb',
    summary: 'Org-wide RAG corpora your agents and the assistant retrieve from.',
    icon: Database,
    body: `The **Knowledge Base (KB)** is your organization's searchable memory. Documents
are broken into chunks, turned into **embeddings** (vectors), and retrieved by
meaning — so an AI agent or the assistant can pull the *relevant* passage even
when the wording differs. This is **RAG** (retrieval-augmented generation).

### What goes in it
- **Uploaded files** — Markdown, PDF, Word, text.
- **Synced Dev13 content** — wiki pages, project pages, and attachments, indexed
  in place (referenced back to the live object, not re-uploaded).

### Scopes (which corpus a document lives in)
- **Organization Knowledge** — the default, org-wide corpus.
- **Project knowledge** — each project gets its own KB, created on first sync.
- **Collection knowledge** — each wiki collection gets its own KB, created on
  first sync.

### Who uses it
- **AI agents** retrieve from it while they work (optionally scoped by KB tags).
- **The assistant** and semantic search read from it.

Everything is **org-scoped** — a KB is never visible to another organization.`,
  },
  {
    slug: 'kb-adding',
    title: 'Adding knowledge',
    module: 'kb',
    summary: 'Upload files, or sync wiki/project pages and attachments into a KB.',
    icon: Upload,
    body: `There are two ways to get content into a KB.

### 1. Upload a file
On **Knowledge Base**, pick a base and click **Upload**. Markdown, PDF, Word and
text are extracted, chunked, embedded and indexed. Add optional **tags** so agents
can scope retrieval to it.

### 2. Sync existing Dev13 content
From a **wiki page**, **project page**, or **attachment**, use **Sync to
Knowledge Base** (the ⋯ menu on a page, or the button by an attachment). The
source is indexed into the right KB and kept **referenced** to the live object —
no duplicate file to maintain.

### The "Synced" indicator
Once a page is synced, its header shows a green **Synced · <date>** badge and the
menu switches to **Re-sync to Knowledge Base**. Attachments show the same state on
their row. Re-sync any time to pull the latest content.

### What can be indexed
Only **public, non-archived** pages are indexed — private or archived pages are
never added to a shared corpus (see **Privacy & access**).`,
  },
  {
    slug: 'kb-scopes',
    title: 'Project & collection knowledge bases',
    module: 'kb',
    summary: 'Batch-sync a whole project or wiki collection into its own KB.',
    icon: Boxes,
    body: `Beyond the org KB, **each project and each wiki collection has its own KB**,
created automatically the first time you sync it. This keeps a space's knowledge
together and lets agents retrieve from just that corpus.

### Sync a whole collection
Open the collection and click **Sync to Knowledge Base** in its header. Every
public, non-archived page in the collection is indexed into that collection's KB
in one background job.

### Sync a whole project
A project's pages and attachments sync into the project's KB the same way.

### Keep a project out of the Knowledge Base
A Founder or Super Admin can turn sync off for a whole project: **Settings →
General → Knowledge Base → Sync to Knowledge Base**, then **Update project**.
Saving it off **removes the project's pages and files from every Knowledge Base**
(documents uploaded straight into the project's KB stay) and hides the sync buttons
on its Pages and Files tabs. Turning it back on doesn't re-sync by itself — press
**Sync to Knowledge Base** when you want the content back.

### Self-healing
A re-sync **reconciles** the KB with the live space:
- new/edited pages are (re-)indexed,
- pages that were deleted, archived, or turned private are **removed**.

So you can re-sync freely — it converges on the current, shareable set. Archived
pages are always skipped.`,
  },
  {
    slug: 'kb-freshness',
    title: 'Keeping knowledge fresh',
    module: 'kb',
    summary: 'Edits re-embed automatically; removed or private content is purged.',
    icon: RefreshCw,
    body: `A synced KB stays in step with its sources — you don't have to remember to
re-sync after every edit.

### Edits
When a synced page is saved, its document is flagged and **re-embedded
automatically** by a background job. Unchanged saves (a reorder or publish that
didn't touch the body) clear without a wasteful re-embed.

### Removals
A synced page is **removed from the KB** when it is:
- **deleted**,
- **archived**, or
- **turned private**.

So private or removed content never lingers in a shared corpus.

### Manual refresh
You can always force it: **Re-sync base** on a KB re-indexes every document, and a
collection/project re-sync reconciles the whole space. Bulk database edits bypass
the automatic hooks — a full re-sync catches anything missed.`,
  },
  {
    slug: 'kb-privacy',
    title: 'Privacy & access',
    module: 'kb',
    summary: 'Private collections and private pages never leak through retrieval.',
    icon: ShieldCheck,
    body: `The KB carries the same visibility rules as the Wiki — indexing content never
widens who can see it.

### Only public content is indexed
Only **public, non-archived** pages enter a KB. Private pages and archived pages
are never indexed; making a page private removes it from the KB.

### Private collections are gated
A **private wiki collection** has its own KB, and that KB is only visible to the
collection's **members** (plus Founders / Super Admins) — in **both**:
- **agent / assistant retrieval** (search results exclude it), and
- the **Knowledge Base browser** (the base and its documents don't appear).

An identity-less caller (e.g. a bot key with no member) sees **public collections
only**.

### Always org-scoped
On top of all this, a KB is never visible across organizations. Retrieval is
tenant-isolated first, then gated by the rules above.`,
  },
  {
    slug: 'kb-search',
    title: 'Searching the knowledge base',
    module: 'kb',
    summary: 'Semantic search over a corpus, optionally scoped by base or tags.',
    icon: Search,
    body: `On **Knowledge Base**, use **Search this knowledge base** to query by meaning —
results are the most relevant chunks, not just keyword matches.

### Scope
- Pick a **base** to search just that corpus (org, a project, or a collection).
- Agents scope retrieval with **KB tags** set on their install, so a marketing
  agent can be pointed at brand-voice/pricing content only.

### If search returns nothing
- The base may have **no indexed documents** yet, or documents are still
  processing.
- If every document shows **Failed — No embedder configured**, an admin needs to
  set the embedding provider (see **Configuring embeddings**).`,
  },
  {
    slug: 'kb-embeddings',
    title: 'Configuring embeddings (admins)',
    module: 'kb',
    summary: "Set your organization's own OpenAI key so documents can be indexed.",
    icon: KeyRound,
    roles: ADMIN_ROLES,
    body: `Indexing and search need an **OpenAI key**, and it's **bring-your-own, per
organization** — there is deliberately **no platform fallback**, so the platform
never pays for your usage. Until you set a key, every document lands as **Failed**
(*"No OpenAI key configured for this organization"*) and search is unavailable.

### Set it (Founder / Super Admin)
Go to **Settings → Integrations → AI Embeddings (OpenAI)**:
1. Paste your OpenAI key and **Save**. It's stored **encrypted**; the key is never
   shown again — only a masked hint (\`…7788\`) and a *Configured* status.
2. Re-index existing content from **Knowledge Base → Re-sync base** (or the ↻ on a
   failed row). Status moves **Failed → Indexed** with chunks > 0.
3. **Remove** clears the key (indexing/search stop until a new one is set).

Billing goes to **your** OpenAI account. Each organization sets its own key.

### The model is fixed platform-wide
Only the **key** is per-tenant; the embedding **model** (\`text-embedding-3-small\`)
is pinned for everyone, because the vector **dimension** (1536) is baked into the
database column. A different-dimension model (e.g. \`text-embedding-3-large\` =
3072) would require a migration and a full re-embed across all tenants — so it's
not a per-org choice.`,
  },
];
